import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import type { ICommonServiceOutput } from "@/Components/Api/CommonServiceOutput";
import Button from "@/design-system/button";
import { Post } from "@/lib/fetch-helper";
import { Success } from "@/src/common/icons/Success";
import { languageSelector } from "@/src/contexts";
import { useMutationCustom } from "@/src/query-client-overrides";
import { PasswordSettingsResources } from "@/src/settings/PasswordSettings.Resources";
import { PasswordSettingsResourcesEn } from "@/src/settings/PasswordSettings.Resources.En";
import { Loading } from "@Components/Loading";
import { Modal } from "@Components/Modal";
import Text from "@Components/Text";
import { TextboxAppearanceV2, TextboxV2 } from "@Components/Textbox";
import { ChangePassword } from "@Routes/AccountLinks";
import * as stylex from "@stylexjs/stylex";

import * as cssPortal from "../shared/Portal.module.css";

export interface IPasswordSettings {
    onClose?: () => void;
}

interface IResetPasswordForm {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const PasswordSettings: FC<IPasswordSettings> = (props) => {
    const resources = languageSelector(PasswordSettingsResources, PasswordSettingsResourcesEn);
    const { register, handleSubmit, formState, getValues } = useForm<IResetPasswordForm>();
    const [customError, setCustomError] = useState<string | null>(null);
    const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);

    const { isPending, mutate } = useMutationCustom({
        mutationFn: (values: IResetPasswordForm) =>
            Post<ICommonServiceOutput, IResetPasswordForm>(ChangePassword, { arg: values }),
        onSuccess: (response) => {
            if (response.resultStatus.errorMessage) {
                setCustomError(response.resultStatus.errorMessage);
            }
            setPasswordChangeSuccess(response?.resultStatus?.success || false);
        },
    });

    const onSubmit = (handler: IResetPasswordForm) => {
        mutate({
            ...handler,
            oldPassword: btoa(encodeURIComponent(handler.oldPassword)),
            newPassword: btoa(encodeURIComponent(handler.newPassword)),
            confirmPassword: btoa(encodeURIComponent(handler.confirmPassword)),
        });
    };

    if (passwordChangeSuccess) {
        return (
            <Modal
                clientId="modal-changePassword-success"
                headerText={resources.title}
                isOpen
                onClose={props.onClose}
                closeOnBlur
            >
                <div {...stylex.props(styles.form)}>
                    <span {...stylex.props(styles.successIcon)}>
                        <Success />
                    </span>
                    <Text textElement="h5" fontStyle="h5">
                        {resources.successTitle}
                    </Text>
                    <Button
                        as="button"
                        variant="magentaRound"
                        size="M"
                        onClick={props.onClose}
                        styleX={styles.button}
                    >
                        {resources.successButton}
                    </Button>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            clientId="modal-changePassword"
            headerText={resources.title}
            isOpen
            onClose={props.onClose}
            closeOnBlur
        >
            <form {...stylex.props(styles.form)} onSubmit={handleSubmit(onSubmit)}>
                {isPending && <Loading isModal />}
                <p>{resources.description}</p>
                <TextboxV2
                    placeholder={resources.currentPasswordText}
                    type="password"
                    autoComplete="current-password"
                    {...register("oldPassword", {
                        required: resources.passwordRequiredError,
                        minLength: { value: 6, message: resources.passwordInvalidSizeError },
                    })}
                    appearance={TextboxAppearanceV2.Standard}
                    errorMessage={formState.errors.oldPassword?.message}
                    className={stylex.props(styles.textbox).className}
                />

                <TextboxV2
                    placeholder={resources.newPasswordText}
                    type="password"
                    autoComplete="new-password"
                    {...register("newPassword", {
                        required: resources.passwordRequiredError,
                        minLength: { value: 6, message: resources.passwordInvalidSizeError },
                        validate: (p) =>
                            p !== getValues("oldPassword") || resources.newPasswordIsTheSameAsOldOne,
                    })}
                    appearance={TextboxAppearanceV2.Standard}
                    errorMessage={formState.errors.newPassword?.message}
                    className={stylex.props(styles.textbox).className}
                />

                <TextboxV2
                    placeholder={resources.confirmPasswordText}
                    type="password"
                    autoComplete="new-password"
                    {...register("confirmPassword", {
                        required: resources.passwordRequiredError,
                        validate: (p) => p === getValues("newPassword") || resources.passwordsNotMatchError,
                    })}
                    appearance={TextboxAppearanceV2.Standard}
                    errorMessage={formState.errors.confirmPassword?.message}
                    className={stylex.props(styles.textbox).className}
                />
                {customError && <p className={cssPortal.hasError}>{customError}</p>}
                <Button as="button" variant="magentaRound" size="M" type="submit" styleX={styles.button}>
                    {resources.confirmButton}
                </Button>
            </form>
        </Modal>
    );
};

const styles = stylex.create({
    form: {
        gap: "1.5rem",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        width: "370px",
    },
    button: {
        width: "100%",
    },
    successIcon: {
        height: "5rem",
        width: "5rem",
    },
    textbox: { width: "300px" },
});

export default PasswordSettings;
